'use client';

import Script from 'next/script';

import { CookiePreferencesLink } from '@components/CookiePreferencesLink/CookiePreferencesLink';
import { Text } from '@components/Text/Text';
import { HUBSPOT_PORTAL_ID, HUBSPOT_REGION } from '@config/constants';
import {
  OSANO_COOKIE_ACCEPTED,
  useCookiePermissions,
} from '@domain/cookies/CookiePermissionsContext';

interface IProps {
  /** The HubSpot form id, found in the form's share/embed dialog. */
  formId: string;
  className?: string;
  portalId?: string;
  region?: string;
}

export const HubSpotForm = ({
  formId,
  className = '',
  portalId = HUBSPOT_PORTAL_ID,
  region = HUBSPOT_REGION,
}: IProps) => {
  const { permissions } = useCookiePermissions();
  const isMarketingAccepted = permissions.MARKETING === OSANO_COOKIE_ACCEPTED;

  // Osano blocks the embed script as marketing and then runs it in place once consent is
  // saved, so the script and the frame stay mounted in both states and only the message
  // around them changes. Unmounting either would race Osano's own injection.
  return (
    <>
      <Script
        id={`hubspot-form-embed-${portalId}`}
        strategy="afterInteractive"
        src={`https://js.hsforms.net/forms/embed/${portalId}.js`}
      />

      {!isMarketingAccepted && (
        <div className={`text-center ${className}`} data-testid="HubSpotFormConsentFallback">
          <Text variant="md">
            Our contact form needs marketing cookies to load. Turn them on in your cookie
            preferences and it&apos;ll show up right here.
          </Text>
          <CookiePreferencesLink variant="buttonPrimary" className="mt-2" />
        </div>
      )}

      {/* min-height reserves space for the injected iframe so it does not shift the page */}
      <div
        className={`hs-form-frame ${isMarketingAccepted ? 'min-h-105' : ''} ${className}`}
        data-form-id={formId}
        data-portal-id={portalId}
        data-region={region}
        data-testid="HubSpotForm"
      />
    </>
  );
};
